import ShareVideoPage from '@anm/components/share/ShareVideoPage';
import { DAY_MS } from '@anm/constants/date';
import useResize from '@anm/hooks/useResize';
import useSwitchState from '@anm/hooks/useSwitchState';
import { useCallback, useEffect, useRef, useState, MouseEvent, RefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import vtConfig from '../../../../../config';
import { EMBED_TAB, LANDING_TAB } from '../../../../../pages/video';
import { analyticsActions, analyticsSelectors } from '../../../../../store/modules/analytics';
import { hostedVideoSelectors } from '../../../../../store/modules/hostedVideo';
import { landingsSelectors } from '../../../../../store/modules/landings';
import { projectSelectors } from '../../../../../store/modules/project';

import { AnalyticsTabProps, NoDataProps } from './types';

const { urls } = vtConfig();

export const useFetchAnalytics = () => {
  const dispatch = useDispatch();
  const isLifetimeChartsWasFetched = useRef(false);
  const [startDate, endDate] = useSelector(analyticsSelectors.selectDateRange);
  const aggregatePlays = useSelector(state => analyticsSelectors.selectAggregateValueByKey(state, 'plays'));
  const isAllChartsWasFetched = useSelector(analyticsSelectors.selectIsAllChartsWasFetched);
  const videoId = useSelector(projectSelectors.selectCurrentProjectId);
  const projectCreatedTime = useSelector(projectSelectors.selectCurrentProjectCreatedTime);
  const isProjectEverRender = useSelector(projectSelectors.selectCurrentProjectEverRendered);

  const [isExistPlays, setExistPlays] = useState(false);

  useEffect(() => {
    setExistPlays((aggregatePlays || 0) > 0);
  }, [aggregatePlays]);

  const hasData = !!aggregatePlays;
  const canNotFetchLifetimeChartsData =
    !(isAllChartsWasFetched && !isLifetimeChartsWasFetched.current) || hasData || !isProjectEverRender || isExistPlays;

  useEffect(() => {
    if (canNotFetchLifetimeChartsData) return;

    isLifetimeChartsWasFetched.current = true;
    const projectLifetimeDatesRange = [projectCreatedTime!, Date.now()];

    dispatch(analyticsActions.updateDateRange(projectLifetimeDatesRange));
  }, [canNotFetchLifetimeChartsData]);

  useEffect(() => {
    if (!videoId || !isProjectEverRender || !startDate || !endDate) return;

    dispatch(analyticsActions.fetchAnalytics());
  }, [videoId, startDate, endDate, isProjectEverRender]);
};

export const useIsVerticalVideo = () => {
  const project = useSelector(projectSelectors.selectCurrentProject);

  const isVideoVertical = !!project && project.width < project.height;

  return isVideoVertical;
};

export const useDateRange = () => {
  const dispatch = useDispatch();
  const dateRange = useSelector(analyticsSelectors.selectDateRange);
  const projectCreatedTime = useSelector(projectSelectors.selectCurrentProjectCreatedTime);

  useEffect(() => {
    const endDate = Date.now();
    const startDate = endDate - DAY_MS * 7;

    dispatch(analyticsActions.updateDateRange([startDate, endDate]));
  }, []);

  const handleDateChange = useCallback((dates: number[]) => {
    dispatch(analyticsActions.updateDateRange(dates));
  }, []);

  return [dateRange, projectCreatedTime, handleDateChange] as const;
};

export const useHeaderMaxWidth = (ref: RefObject<HTMLDivElement>, horizontalOffset: number) => {
  const [maxWidth, setMaxWidth] = useState(0);

  useEffect(() => {
    const elementWidth = ref?.current?.clientWidth;
    elementWidth && setMaxWidth(elementWidth - horizontalOffset);
  }, [ref?.current]);

  useResize(() => {
    const elementWidth = ref?.current?.clientWidth;
    elementWidth && setMaxWidth(elementWidth - horizontalOffset);
  });

  return maxWidth;
};

export const useNoDataHandlers = ({ onSwitchTab }: AnalyticsTabProps) => {
  const videoId = useSelector(projectSelectors.selectCurrentProjectId);

  const isPublicLanding = useSelector(landingsSelectors.selectLandingPublicStatus);
  const shareLink = `${urls.videoLanding}${videoId}`;
  const landingTabUrl = `${urls.wave}video/${videoId}/landing`;

  const navigateToLandingTab = useCallback((evt: MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    onSwitchTab(LANDING_TAB);
  }, []);

  const navigateToEmbedTab = useCallback((evt: MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    onSwitchTab(EMBED_TAB);
  }, []);

  return [videoId, shareLink, landingTabUrl, navigateToEmbedTab, isPublicLanding, navigateToLandingTab] as const;
};

export const useNoDataMessage = (props: NoDataProps) => {
  const [
    videoId,
    shareLink,
    landingTabUrl,
    navigateToEmbedTab,
    isPublicLanding,
    navigateToLandingTab
  ] = useNoDataHandlers(props);
  const [isModalOpen, openModal, closeModal] = useSwitchState();

  const isLifetimeEmpty = useSelector(analyticsSelectors.selectPeriodDataIsLifetimeEmpty);
  const isEmbedded = useSelector(hostedVideoSelectors.selectEmbedStatus);
  const canShowMessage = isLifetimeEmpty && (!isPublicLanding || !isEmbedded);

  const renderMessage = (isOneRow = false) => (
    <>
      {isModalOpen && <ShareVideoPage {...{ shareLink, landingTabUrl }} onClose={closeModal} />}
      {!isPublicLanding && (
        <a onClick={navigateToLandingTab} href={`${urls.wave}video/${videoId}/landing`}>
          Turn on and&nbsp;
        </a>
      )}
      <a onClick={openModal}>{`${isPublicLanding ? 'Share' : 'share'}`}</a>
      &nbsp;your Landing page, or&nbsp;
      <a onClick={navigateToEmbedTab} href={`${urls.wave}video/${videoId}/embed`}>
        embed your video
      </a>
      .{!isOneRow && <br />} Once the video gets views, the data will appear here.
    </>
  );

  return [canShowMessage, renderMessage] as const;
};
